import type { ConsultationModel } from '@/api/consultations/Consultation.types';
import { CompleteConsultationModal } from '@/components/PatientPageV2/components/ConsultationTabSections/CompleteConsultationModal';
import { DoctorConsultActions } from '@/components/PatientPageV2/components/ConsultationTabSections/DoctorConsultActions/DoctorConsultActions';
import type { ShowConsultation } from '@/components/PatientPageV2/components/ConsultationTabSections/ShowConsultation';
import { ConsultationStatus } from '@/data/constants';
import { getUser } from '@/data/service/authService';
import { useConsultActions } from '@/hooks/consult/useConsultActions';
import type { SaveConsultationFunc, SaveConsultationOptions } from '@/hooks/consult/useSaveConsultation';
import type { SaveConsultationParams } from '@/hooks/consult/useSaveConsultationData';
import { useGetConsultationStatus } from '@/hooks/rest/useGetConsultationStatus';
import { useAppStore } from '@/state-management';
import { useCallback } from 'react';
import { closeBrowserWindow } from '../utils/closeBrowserWindow';

interface ConsultActionsProps {
  consultation: ShowConsultation | null;
  isConcessionPatient: boolean;
  isPatientActive: boolean;
  saveConsult: SaveConsultationFunc;
  submitCreatePrescription: () => Promise<Response | null>;
  isProcessingAction?: boolean;
}

export const ConsultActions = ({
  consultation,
  isConcessionPatient,
  isPatientActive,
  saveConsult,
  submitCreatePrescription,
  isProcessingAction,
}: ConsultActionsProps) => {
  const store = useAppStore();
  const { email: patientEmail } = store.getPatientContactDetails();
  const user = getUser();
  const isComplimentary = store.guidedDoctorConsult.fields.complimentary;
  const followUpWeekPeriod = store.guidedDoctorConsult.fields.followUpWeekPeriod;

  const { consultationStatuses } = useGetConsultationStatus(consultation as unknown as ConsultationModel);

  const isMatchingDoctor = consultation?.Doctor?.DoctorUser.id === user?.id;
  const isConsultationReScheduled = consultation?.ConsultationStatus?.id === ConsultationStatus.re_scheduled;
  const showActions = consultation && isMatchingDoctor && !isConsultationReScheduled;

  const saveConsultatioNotes = async () => {
    if (consultation) {
      await saveConsult(
        {
          id: consultation.id,
          notes: consultation.notes,
          status_id: consultation.status_id ?? 0,
          sub_status_id: consultation.sub_status_id ?? 0,
        },
        {
          askConfirmation: false,
        },
      );
    }
  };

  const {
    loadingAnyPayment,
    loadingConsultCompletion,
    isCompleteConsultationModalOpen,
    handleConsultPayment,
    handleNoShow,
    handleClearPrescription,
    handleShopifyPaymentRequest,
    handleCloseCompleteConsultationModal,
    handleConfirmCompleteConsult,
    handleCompleteConsultation,
  } = useConsultActions({
    consult: consultation as ShowConsultation,
    isConcessionPatient: isConcessionPatient,
    patientEmail: patientEmail,
    patientActive: isPatientActive,
    saveConsult,
    submitCreatePrescription,
    onPrescriptionActionComplete: closeBrowserWindow,
    isComplimentary,
    nextConsultationInWeeks: followUpWeekPeriod ?? undefined,
  });

  const handleNoShowAndClose = useCallback(async () => {
    await handleNoShow();
    await closeBrowserWindow();
  }, [handleNoShow]);

  const handleOnSubmit: SaveConsultationFunc = useCallback(
    async (consultationData: SaveConsultationParams | null, options?: SaveConsultationOptions) => {
      await saveConsult(consultationData, options);
      await closeBrowserWindow();
    },
    [saveConsult],
  );

  const handleIsComplimentaryChangeAndStore = useCallback(() => {
    store.setFieldsState('complimentary', !isComplimentary);
  }, [isComplimentary, store]);

  if (!showActions) {
    return null;
  }

  return (
    <>
      <DoctorConsultActions
        showConsultation={consultation}
        patientActive={isPatientActive}
        isComplimentary={isComplimentary}
        handleIsComplimentaryChange={handleIsComplimentaryChangeAndStore}
        handleResendInvoice={handleConsultPayment}
        loadingPaymentAction={loadingAnyPayment || !!isProcessingAction}
        handleNoShow={handleNoShowAndClose}
        handleCancelScript={handleClearPrescription}
        handleShopifyPayment={handleShopifyPaymentRequest}
        handleCompleteConsultation={handleCompleteConsultation}
        isSavingConsultation={loadingConsultCompletion}
        handleSaveConsultation={saveConsultatioNotes}
        consultationStatuses={consultationStatuses}
        handleSubmit={handleOnSubmit}
      />
      <CompleteConsultationModal
        isOpen={isCompleteConsultationModalOpen}
        onClose={handleCloseCompleteConsultationModal}
        onConfirm={handleConfirmCompleteConsult}
      />
    </>
  );
};
